import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert, Box, Button, ButtonGroup,
} from '@mui/material';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { sitePin, vehiclePin } from './mapPin';
import { AREA_COLOUR } from './areas';

let loading;
const emptyTrail = [];
// Shared so the area editor reuses this single loader; two <script> tags for the Maps
// API on one page makes it warn and can double-initialise.
export const loadGoogle = (key) => {
  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timeout = setTimeout(() => reject(new Error('Google Maps did not respond.')), 15000);
      window.operationsMapsReady = () => { clearTimeout(timeout); resolve(window.google.maps); };
      script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({ key, callback: 'operationsMapsReady', loading: 'async', v: 'weekly' })}`;
      script.async = true; script.onerror = () => { clearTimeout(timeout); reject(new Error('Google Maps could not load.')); }; document.head.appendChild(script);
    });
  }
  return loading;
};
const valid = (p) => p && p.latitude != null && p.longitude != null && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)) && Math.abs(p.latitude) <= 90 && Math.abs(p.longitude) <= 180 && p.valid !== false;
// Trucks gather: a yard holds twenty within a few hundred metres, and a country holds them a
// thousand kilometres apart. Collapsing them into a numbered circle keeps both readable, and
// clustering stops well before plant zoom so a gate and a weighbridge never merge into one dot.
const clusterRenderer = (AdvancedMarkerElement) => ({
  render: ({ count, position }) => {
    const bubble = document.createElement('div');
    bubble.textContent = String(count);
    bubble.setAttribute('aria-label', `${count} map markers here (vehicles or checkpoints)`);
    bubble.title = `${count} map markers — zoom in to distinguish vehicles and checkpoints`;
    Object.assign(bubble.style, {
      background: '#123247',
      color: '#fff',
      width: count > 99 ? '46px' : '38px',
      height: count > 99 ? '46px' : '38px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      font: '600 13px Roboto, sans-serif',
      border: '2px solid white',
      boxShadow: '0 3px 12px #0005',
    });
    return new AdvancedMarkerElement({ position, content: bubble, zIndex: 1000 });
  },
});

const GoogleOperationsMap = ({
  config, points = [], trail = [], plannedTrail = emptyTrail, areas = [], selectedId, onSelect,
  card = null, height = 520, onFallback, follow = null, frame = null,
}) => {
  const host = useRef(null); const mapRef = useRef(null); const api = useRef(null); const markers = useRef([]); const line = useRef(null); const shapes = useRef([]); const failed = useRef(false);
  const cluster = useRef(null); const info = useRef(null); const byId = useRef(new Map());
  // Markers are kept between refreshes and moved, so the map does not flash twice a minute.
  const pins = useRef(new Map()); const sitePins = useRef(new Map()); const clustered = useRef(new Set()); const anchored = useRef(null);
  // The click handler is attached once per marker and must always call the current callback.
  const choose = useRef(onSelect); choose.current = onSelect;
  const [ready, setReady] = useState(false); const [error, setError] = useState(''); const [satellite, setSatellite] = useState(false);
  useEffect(() => {
    if (!ready || failed.current) return undefined;
    const planned = new api.current.maps.Polyline({ map: mapRef.current, path: plannedTrail.filter(valid).map((p) => ({ lat: Number(p.latitude), lng: Number(p.longitude) })), strokeOpacity: 0, zIndex: 2, icons: [{ icon: { path: 'M 0,-1 0,1', strokeColor: '#64748b', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '14px' }] });
    return () => planned.setMap(null);
  }, [ready, plannedTrail]);
  // The card is React, the info window wants a DOM node, so one node is kept and rendered into.
  const [cardHost] = useState(() => (typeof document === 'undefined' ? null : document.createElement('div')));
  useEffect(() => {
    let cancelled = false; const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => { failed.current = true; if (!cancelled) { setReady(false); setError('Google Maps authorization failed. Check the configured key and website restrictions.'); } };
    loadGoogle(config.googleMapsApiKey).then(async (maps) => {
      const { Map } = await maps.importLibrary('maps'); const { AdvancedMarkerElement } = await maps.importLibrary('marker');
      if (cancelled || failed.current) return;
      api.current = { maps, AdvancedMarkerElement };
      mapRef.current = new Map(host.current, { center: { lat: 20.59, lng: 78.96 }, zoom: 4, mapId: config.googleMapId || 'DEMO_MAP_ID', mapTypeControl: false, streetViewControl: false, fullscreenControl: true, gestureHandling: 'cooperative' }); setReady(true);
    }).catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; window.gm_authFailure = previousAuthFailure; markers.current.forEach((m) => { m.map = null; }); shapes.current.forEach((s) => s.setMap(null)); line.current?.setMap(null); if (mapRef.current) api.current.maps.event.clearInstanceListeners(mapRef.current); mapRef.current = null; };
  }, [config.googleMapsApiKey, config.googleMapId]);
  useEffect(() => {
    if (!ready || failed.current) return;
    const { AdvancedMarkerElement } = api.current;
    // Places and trucks are drawn from the same list but must not be counted together: only the
    // fleet is handed to the clusterer, so a customer gate never becomes one of "3 vehicles here".
    const shown = points.filter((p) => valid(p) && p.kind !== 'site');
    const sites = points.filter((p) => valid(p) && p.kind === 'site');
    cluster.current = cluster.current || new MarkerClusterer({ map: mapRef.current, markers: [], renderer: clusterRenderer(AdvancedMarkerElement) });

    // Each truck keeps its marker for as long as it is on the map: moved, relabelled and
    // recoloured in place rather than replaced.
    shown.forEach((p) => {
      const chosen = p.id === selectedId;
      let pin = pins.current.get(p.id);
      // What the marker should look like. Rebuilt only when that changes: a marker replaced on
      // every fix is the flicker this map used to have.
      const shape = `${p.label}|${p.color || ''}|${chosen}`;
      if (!pin) {
        const content = vehiclePin(p, chosen);
        content.addEventListener('click', () => choose.current?.(p.id));
        pin = { marker: new AdvancedMarkerElement({ content, position: { lat: Number(p.latitude), lng: Number(p.longitude) } }), content, shape };
        pins.current.set(p.id, pin);
      } else if (pin.shape !== shape) {
        const content = vehiclePin(p, chosen);
        content.addEventListener('click', () => choose.current?.(p.id));
        pin.marker.content = content; pin.content = content; pin.shape = shape;
      }
      pin.marker.position = { lat: Number(p.latitude), lng: Number(p.longitude) };
      pin.marker.title = p.label;
    });
    const here = new Set(shown.map((p) => p.id));
    [...pins.current.keys()].filter((id) => !here.has(id)).forEach((id) => {
      const gone = pins.current.get(id);
      cluster.current.removeMarker(gone.marker, true); clustered.current.delete(id);
      gone.marker.map = null; pins.current.delete(id);
    });

    // Places move only when somebody edits them, so they are keyed by where they are.
    const placedKeys = new Set(sites.map((s) => `${s.id}:${s.latitude},${s.longitude}`));
    sites.forEach((site) => {
      const key = `${site.id}:${site.latitude},${site.longitude}`;
      if (sitePins.current.has(key)) return;
      sitePins.current.set(key, new AdvancedMarkerElement({
        position: { lat: Number(site.latitude), lng: Number(site.longitude) }, title: site.title || site.label, content: sitePin(site), map: mapRef.current,
      }));
    });
    [...sitePins.current.keys()].filter((key) => !placedKeys.has(key)).forEach((key) => {
      sitePins.current.get(key).map = null; sitePins.current.delete(key);
    });

    // Whoever has just been chosen is never collapsed into a cluster: they stand on the map in
    // their own right, above the others, so the highlight shows and the card has something to hang
    // from. Everything else clusters as before.
    pins.current.forEach((pin, id) => {
      const wanted = id !== selectedId;
      if (wanted === clustered.current.has(id)) return;
      if (wanted) {
        pin.marker.zIndex = null; pin.marker.map = null;
        cluster.current.addMarker(pin.marker, true); clustered.current.add(id);
      } else {
        cluster.current.removeMarker(pin.marker, true); clustered.current.delete(id);
        // Above every other marker and above the cluster bubbles, so a truck picked from the list
        // is never hidden under one parked a few metres away.
        pin.marker.map = mapRef.current; pin.marker.zIndex = 10000;
      }
    });
    // The clusterer recomputes its bubbles from where the markers are now.
    cluster.current.render();
    byId.current = new Map([...pins.current].map(([id, pin]) => [id, pin.marker]));
    markers.current = [...[...pins.current.values()].map((pin) => pin.marker), ...sitePins.current.values()];

    // One line, redrawn by moving its points, so the recorded trail does not flash either.
    const path = trail.filter(valid).map((p) => ({ lat: Number(p.latitude), lng: Number(p.longitude) }));
    if (!line.current) line.current = new api.current.maps.Polyline({ map: mapRef.current, strokeColor: '#1684ff', strokeWeight: 4, strokeOpacity: 0.9, zIndex: 3 });
    line.current.setPath(path);
  }, [ready, points, trail, selectedId]);
  useEffect(() => {
    if (!ready || failed.current || !api.current || !cardHost) return;
    const anchor = byId.current.get(selectedId);
    if (!card || !anchor) { info.current?.close(); anchored.current = null; return; }
    if (!info.current) {
      info.current = new api.current.maps.InfoWindow({ maxWidth: 320 });
      // Dismissing the card is the same act as dropping the selection.
      info.current.addListener('closeclick', () => choose.current?.(null));
    }
    info.current.setContent(cardHost);
    // Reopening an already-open window makes it flash, and the card is re-rendered on every poll.
    // The window follows its marker on its own, so it is opened once per marker.
    if (anchored.current !== anchor) { info.current.open({ map: mapRef.current, anchor }); anchored.current = anchor; }
  }, [ready, selectedId, card, points, onSelect, cardHost]);
  // Areas are drawn beneath the vehicles as context and are never clickable, so they
  // cannot swallow a tap meant for a marker.
  useEffect(() => {
    if (!ready || failed.current) return;
    shapes.current.forEach((s) => s.setMap(null));
    const { maps } = api.current;
    shapes.current = areas.map((a) => {
      const colour = AREA_COLOUR[a.type] || AREA_COLOUR.PLANT;
      const style = { map: mapRef.current, clickable: false, strokeColor: colour, fillColor: colour, fillOpacity: 0.12, strokeWeight: 2, zIndex: 1 };
      return a.shape === 'POLYGON'
        ? new maps.Polygon({ ...style, paths: (a.points || []).map((p) => ({ lat: p.lat, lng: p.lon })) })
        : new maps.Circle({ ...style, center: { lat: a.latitude, lng: a.longitude }, radius: a.radiusMeters });
    });
  }, [ready, areas]);
  const fit = () => {
    const all = [...points.filter((p) => !plannedTrail.length || p.id === selectedId), ...trail, ...plannedTrail].filter(valid);
    if (!ready || !all.length) return;
    const bounds = new api.current.maps.LatLngBounds();
    all.forEach((p) => bounds.extend({ lat: Number(p.latitude), lng: Number(p.longitude) }));
    const corner = bounds.getNorthEast(); const opposite = bounds.getSouthWest();
    // Anything inside roughly two hundred metres is one place, not an extent to be framed.
    const tight = Math.abs(corner.lat() - opposite.lat()) < 0.002 && Math.abs(corner.lng() - opposite.lng()) < 0.002;
    if (tight) { mapRef.current.setCenter(bounds.getCenter()); mapRef.current.setZoom(16); return; }
    mapRef.current.fitBounds(bounds, 70);
  };
  const pointKey = points.filter(valid).map((p) => p.id).join(',');
  // While a journey is being framed, replay owns the camera. The ordinary fit would otherwise pull
  // the view back to the markers every time the truck moved.
  const framing = (frame || []).filter(valid).length > 1;
  useEffect(() => { if (!framing) fit(); }, [ready, pointKey, plannedTrail, framing]);

  // The whole journey, framed once per route rather than once per marker. Keyed on the route's own
  // extent - how many fixes and which ends - because playback is a single marker whose id never
  // changes, so a key built from ids never fires and the truck drives off a map that never moves.
  const frameKey = framing
    ? `${frame.length}:${frame[0]?.fixTime || ''}:${frame[frame.length - 1]?.fixTime || ''}`
    : '';
  useEffect(() => {
    if (!ready || !frameKey) return;
    const route = frame.filter(valid);
    const bounds = new api.current.maps.LatLngBounds();
    route.forEach((p) => bounds.extend({ lat: Number(p.latitude), lng: Number(p.longitude) }));
    mapRef.current.fitBounds(bounds, 70);
    // A short journey would otherwise be framed at street level, where one pan covers the lot.
    api.current.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
      if (mapRef.current && mapRef.current.getZoom() > 15) mapRef.current.setZoom(15);
    });
  }, [ready, frameKey]);

  // And kept in view while it runs - only once it has actually left the view, so somebody who has
  // paused to study a junction can pan away without being dragged back.
  useEffect(() => {
    if (!ready || !valid(follow)) return;
    const at = { lat: Number(follow.latitude), lng: Number(follow.longitude) };
    const bounds = mapRef.current.getBounds();
    if (bounds && bounds.contains(at)) return;
    mapRef.current.panTo(at);
  }, [ready, follow?.latitude, follow?.longitude]);
  useEffect(() => { if (ready) mapRef.current.setMapTypeId(satellite ? 'hybrid' : 'roadmap'); }, [ready, satellite]);
  useEffect(() => { const p = points.find((x) => x.id === selectedId); if (ready && valid(p)) { mapRef.current.panTo({ lat: Number(p.latitude), lng: Number(p.longitude) }); mapRef.current.setZoom(Math.max(mapRef.current.getZoom(), 13)); } }, [ready, selectedId]);
  return (
    <Box sx={{ height, position: 'relative', bgcolor: (t) => (t.palette.mode === 'dark' ? '#1B2733' : '#e5edf3'), borderRadius: 2, overflow: 'hidden' }}>
      {/* Sized explicitly, like the raster map: a map provider sets its own position on the
          element it is handed, which beats the absolute positioning inset: 0 depends on, and the
          container then collapses to zero height with the map drawing where nobody can see it. */}
      <Box ref={host} sx={{ width: '100%', height: '100%' }} />
      {card && cardHost && createPortal(card, cardHost)}
      <ButtonGroup size="small" sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'background.paper' }}>
        <Button variant={!satellite ? 'contained' : 'outlined'} onClick={() => setSatellite(false)}>Map</Button>
        <Button variant={satellite ? 'contained' : 'outlined'} onClick={() => setSatellite(true)}>Satellite</Button>
        <Button onClick={fit}>{plannedTrail.length ? 'Fit route' : points.some((p) => p.kind === 'site') ? 'Fit all' : 'Fit fleet'}</Button>
      </ButtonGroup>
      {!ready && !error && <Alert severity="info" sx={{ position: 'absolute', bottom: 40, left: 12 }}>Loading Google Maps…</Alert>}
      {error && <Alert severity="warning" action={<Button onClick={onFallback}>Use standard map</Button>} sx={{ position: 'absolute', bottom: 40, left: 12, right: 12 }}>{error}</Alert>}
      {/* Areas are map content too: claiming nothing is mapped while boundaries are
          drawn on screen contradicts what the user is looking at. */}
      {ready && !points.some(valid) && areas.length === 0 && <Alert severity="info" sx={{ position: 'absolute', bottom: 40, left: 12, right: 60 }}>No vehicle or checkpoint coordinates are available yet.</Alert>}
      {ready && !points.some(valid) && areas.length > 0 && <Alert severity="info" sx={{ position: 'absolute', bottom: 40, left: 12, right: 60 }}>No vehicles are reporting a position in view. Area boundaries are shown.</Alert>}
    </Box>
  );
};
export default GoogleOperationsMap;
